/**
 * @fileoverview Root application component
 * @description Sets up routing and authentication context
 * @module App
 */

import { lazy, Suspense } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from '@/components/ui/toaster';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { AuthProvider } from '@/hooks/AuthProvider';
import { useAuth } from '@/hooks/useAuth';
import { ClientList } from '@/routes/clients/ClientList';
import { Login } from '@/routes/auth/Login';

// Login and ClientList are the landing pages and stay in the entry chunk.
// Every other screen downloads when first opened.
const ClientDetail = lazy(() => import('@/routes/clients/ClientDetail').then((m) => ({ default: m.ClientDetail })));
const ClientWorkoutDetail = lazy(() => import('@/routes/clients/ClientWorkoutDetail').then((m) => ({ default: m.ClientWorkoutDetail })));
const ProgramEditor = lazy(() => import('@/routes/clients/ProgramEditor').then((m) => ({ default: m.ProgramEditor })));
const Account = lazy(() => import('@/routes/settings/Account').then((m) => ({ default: m.Account })));
const InvoiceListPage = lazy(() => import('@/routes/invoices/InvoiceList').then((m) => ({ default: m.InvoiceListPage })));
const InvoiceSettingsPage = lazy(() => import('@/routes/invoices/InvoiceSettings').then((m) => ({ default: m.InvoiceSettingsPage })));
const InvoiceEditorPage = lazy(() => import('@/routes/invoices/InvoiceEditor').then((m) => ({ default: m.InvoiceEditorPage })));
const Register = lazy(() => import('@/routes/auth/Register').then((m) => ({ default: m.Register })));
const ForgotPassword = lazy(() => import('@/routes/auth/ForgotPassword').then((m) => ({ default: m.ForgotPassword })));
const ResetPassword = lazy(() => import('@/routes/auth/ResetPassword').then((m) => ({ default: m.ResetPassword })));

function FullPageSpinner() {
  return (
    <div className="flex h-screen items-center justify-center">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
    </div>
  );
}

/**
 * Protected route wrapper
 * @param children - Child components to render if authenticated
 */
function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { user, trainerProfile, loading, signOut } = useAuth();

  if (loading) {
    return <FullPageSpinner />;
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // Admin dashboard is trainer-only. A normal client account should not access it.
  if (!trainerProfile) {
    return (
      <div className="flex h-screen items-center justify-center p-6">
        <div className="max-w-md w-full rounded-2xl border border-border/50 bg-card/40 p-6">
          <h1 className="text-xl font-semibold">Trainer access required</h1>
          <p className="mt-2 text-sm text-muted-foreground">
            This dashboard is only available to trainer accounts. If you believe this is a mistake,
            sign out and register as a trainer.
          </p>
          <div className="mt-4 flex justify-end">
            <button
              className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground"
              onClick={() => void signOut()}
            >
              Sign out
            </button>
          </div>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}

export default function App() {
  return (
    <AuthProvider>
      <Suspense fallback={<FullPageSpinner />}>
        <Routes>
          {/* Public routes */}
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />

          {/* Protected routes */}
          <Route
            path="/"
            element={
              <ProtectedRoute>
                <DashboardLayout />
              </ProtectedRoute>
            }
          >
            <Route index element={<ClientList />} />
            <Route path="account" element={<Account />} />
            <Route path="invoices" element={<InvoiceListPage />} />
            <Route path="invoices/settings" element={<InvoiceSettingsPage />} />
            <Route path="invoices/new" element={<InvoiceEditorPage />} />
            <Route path="invoices/:invoiceId" element={<InvoiceEditorPage />} />
            <Route path="clients" element={<Navigate to="/" replace />} />
            <Route path="clients/:id" element={<ClientDetail />} />
            <Route path="clients/:id/workouts/:scheduledWorkoutId" element={<ClientWorkoutDetail />} />
            <Route path="clients/:id/program" element={<ProgramEditor />} />
          </Route>

          {/* Fallback */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Suspense>
      <Toaster />
    </AuthProvider>
  );
}
